import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getDataTestId } from '../../../lib/props';
import { cn } from '../../../lib/utils';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from '../../ui/sheet';
import { SpecraOverlayFooter } from '../lieutenants/overlay-footer';
import { SpecraOverlayHeader } from '../lieutenants/overlay-header';
const widthClass = {
    sm: 'w-full sm:max-w-sm',
    md: 'w-full sm:max-w-lg',
    lg: 'w-full sm:max-w-2xl',
    xl: 'w-full sm:max-w-5xl'
};
// Rounded on the INNER edge only — the outer edge is flush with the viewport, so
// rounding it would leave two slivers of whatever is behind. Same treatment the
// app shell gives its content area (`rounded-tl-xl`), which is what makes a
// drawer read as a panel of this product rather than a browser dialog.
const radiusClass = {
    left: 'rounded-r-[var(--radius-xl)]',
    right: 'rounded-l-[var(--radius-xl)]'
};
/** Controlled detail/form drawer used across record and delivery workflows. */ export function SpecraOverlayDrawer({ children, className, contentProps, contentRef, defaultOpen, modal = true, description, eyebrow, footer, footerProps, headerActions, bodyProps, bodyRef, focusableBody = true, onOpenChange, open, portalContainer, scrollBody = true, side = 'right', testId, title, trigger, width = 'md' }) {
    return /*#__PURE__*/ _jsxs(Sheet, {
        defaultOpen: defaultOpen,
        modal: modal,
        onOpenChange: onOpenChange,
        open: open,
        children: [
            trigger ? /*#__PURE__*/ _jsx(SheetTrigger, {
                asChild: true,
                children: trigger
            }) : null,
            /*#__PURE__*/ _jsxs(SheetContent, {
                ...contentProps,
                className: cn('flex flex-col gap-0 bg-card p-0', radiusClass[side], widthClass[width], contentProps?.className, className),
                "data-testid": testId ?? getDataTestId(contentProps),
                portalContainer: portalContainer,
                ref: contentRef,
                side: side,
                children: [
                    /*#__PURE__*/ _jsx(SpecraOverlayHeader, {
                        actions: headerActions,
                        // SheetContent's built-in close sits at `right-4 top-4` regardless of
                        // breakpoint (16px inset, 16px square) -- reserve past its left edge
                        // (32px + a gap) so header actions can never render underneath it.
                        className: "pr-10 sm:pr-10",
                        descriptionAsChild: true,
                        eyebrow: eyebrow,
                        description: description ? /*#__PURE__*/ _jsx(SheetDescription, {
                            className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                            children: description
                        }) : undefined,
                        title: /*#__PURE__*/ _jsx(SheetTitle, {
                            className: "font-display text-[length:var(--fs-ui-title)] font-medium leading-tight tracking-[var(--ls-display)] text-foreground",
                            children: title
                        }),
                        titleAsChild: true
                    }),
                    !description ? /*#__PURE__*/ _jsx(SheetDescription, {
                        className: "sr-only",
                        children: typeof title === 'string' ? `${title} drawer` : 'Drawer content'
                    }) : null,
                    /*#__PURE__*/ _jsx("div", {
                        ...bodyProps,
                        "aria-label": focusableBody ? bodyProps?.['aria-label'] ?? (typeof title === 'string' ? `${title} content` : 'Drawer content') : bodyProps?.['aria-label'],
                        className: cn(// flex-col so scrollBody=false hosts can pin a footer and scroll a flex-1 child.
                        'flex min-h-0 flex-1 flex-col px-4 py-3 sm:px-5 sm:py-4', scrollBody && 'overflow-y-auto', bodyProps?.className),
                        ref: bodyRef,
                        role: focusableBody ? bodyProps?.role ?? 'group' : bodyProps?.role,
                        tabIndex: focusableBody && scrollBody ? bodyProps?.tabIndex ?? 0 : bodyProps?.tabIndex,
                        children: children
                    }),
                    footerProps ? /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                        ...footerProps
                    }) : footer
                ]
            })
        ]
    });
}
